
import React, { useState, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ContactsList from './ContactsList';
import ContactDetails from './ContactDetails';
import CommunicationCenter from './CommunicationCenter';
import IntegrationsPanel from './IntegrationsPanel';
import { CustomerContact, CustomerMessage } from '@/types/resellerCustomer';
import { generateSampleContacts, generateSampleMessages } from './crmSampleData';

const ResellerCRM: React.FC = () => {
  const [activeTab, setActiveTab] = useState('contacts');
  const [selectedContact, setSelectedContact] = useState<CustomerContact | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Generate sample data
  const contacts = useMemo(() => generateSampleContacts(30), []);
  const messages = useMemo(() => generateSampleMessages(contacts), [contacts]);
  
  // Filter contacts based on search and status
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      const searchMatch = 
        contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phone.includes(searchTerm) ||
        contact.company.toLowerCase().includes(searchTerm.toLowerCase());
      
      const statusMatch = statusFilter === 'all' || contact.status === statusFilter;
      
      return searchMatch && statusMatch;
    });
  }, [contacts, searchTerm, statusFilter]);
  
  // Filter messages for the selected contact
  const contactMessages = useMemo(() => {
    if (!selectedContact) return [];
    return messages.filter(message => message.customerId === selectedContact.id);
  }, [messages, selectedContact]);
  
  // Handle contact selection
  const handleSelectContact = (contact: CustomerContact) => {
    setSelectedContact(contact);
    setActiveTab('details');
  };
  
  // Handle sending a new message
  const handleSendMessage = (message: Omit<CustomerMessage, 'id' | 'sentAt'>) => {
    // In a real implementation, this would send a message to the backend
    console.log('Sending message:', message);
    // For now, we'll just return to show it sent successfully
    return Promise.resolve(true);
  };
  
  return (
    <div className="grid grid-cols-1 gap-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 mb-6">
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="details" disabled={!selectedContact}>
            Contact Details
          </TabsTrigger>
          <TabsTrigger value="communications" disabled={!selectedContact}>
            Communications
          </TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
        </TabsList>
        
        <TabsContent value="contacts">
          <ContactsList 
            contacts={filteredContacts} 
            onSelectContact={handleSelectContact}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        </TabsContent>
        
        <TabsContent value="details">
          {selectedContact ? (
            <ContactDetails 
              contact={selectedContact}
              onBack={() => setActiveTab('contacts')}
            />
          ) : (
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">Select a contact to view details</p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="communications">
          {selectedContact ? (
            <CommunicationCenter 
              contact={selectedContact}
              messages={contactMessages}
              onSendMessage={handleSendMessage}
              onBack={() => setActiveTab('details')}
            />
          ) : (
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">Select a contact to view communications</p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="integrations">
          <IntegrationsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ResellerCRM;
